import React, { useState, useRef } from 'react';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { ImageCarousel } from "../components/ui/image-carousel";

// Initialize EmailJS (add this near the top of your file, after imports)
emailjs.init(import.meta.env.VITE_EMAILJS_PUBLIC_KEY);

const contactInfo = {
  address: "Șoseaua Colentina nr. 16, Complex Rose Garden, Bloc B4, parter",
  phones: ["0726 423 784", "0723 696 188"],
  email: "contact@centrulprogres.com",
  program: "Program Flexibil",
  companyName: "Centru Progres S.R.L.",
  mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2848.8454657081584!2d26.115699776891713!3d44.46020647107631!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x40b1f8c8a65505af%3A0x7f3e3f3c3f3c3f3c!2sSoseaua+Colentina+16%2C+Bucure%C8%99ti!5e0!3m2!1sro!2sro!4v1620000000000!5m2!1sro!2sro",
  socials: {
    instagram: "https://www.instagram.com/centru.progres?igsh=bG1mZWgwZjdld2d3",
    facebook: "https://m.facebook.com/centru.progres/"
  }
};

const Contact = () => {
  const form = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    user_name: '',
    user_email: '',
    user_phone: '',
    service_type: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.current) return;

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      const result = await emailjs.sendForm(
        import.meta.env.VITE_EMAILJS_SERVICE_ID,
        import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
        form.current,
        import.meta.env.VITE_EMAILJS_PUBLIC_KEY
      );

      if (result.text === 'OK') {
        setSubmitStatus({
          type: 'success',
          message: 'Mesajul a fost trimis cu succes! Vă vom contacta în curând.'
        });
        setFormData({ user_name: '', user_email: '', user_phone: '', service_type: '', message: '' });
      }
    } catch (err: unknown) {
      console.error('Error sending email:', err);
      setSubmitStatus({
        type: 'error',
        message: 'A apărut o eroare. Vă rugăm să încercați din nou sau să ne contactați telefonic.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-surface py-20 sm:py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="mb-12 max-w-3xl">
          <p className="eyebrow">Contact și locație</p>
          <h1 className="page-heading mt-5 font-bold">Spune-ne ce curs cauți.</h1>
          <p className="mt-5 text-xl text-gray-600">Ne poți scrie, suna sau vizita în Colentina. Îți răspundem cu opțiunile potrivite pentru clasă și materie.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Contact Form */}
          <div className="rounded-[2rem] border border-blue-100 bg-white p-7 shadow-float sm:p-9">
            <h2 className="text-2xl font-semibold mb-4">Trimite-ne un mesaj</h2>
            <form ref={form} onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-700 mb-1" htmlFor="user_name">Nume complet</label>
                <input
                  type="text"
                  id="user_name"
                  name="user_name"
                  value={formData.user_name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Numele tău"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-1" htmlFor="user_email">Email</label>
                <input
                  type="email"
                  id="user_email"
                  name="user_email"
                  value={formData.user_email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="email@exemplu.com"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-1" htmlFor="user_phone">Număr de telefon</label>
                <input
                  type="tel"
                  id="user_phone"
                  name="user_phone"
                  value={formData.user_phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0712 345 678"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-1" htmlFor="service_type">Serviciu dorit</label>
                <select
                  id="service_type"
                  name="service_type"
                  value={formData.service_type}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  required
                >
                  <option value="">Selectează un serviciu</option>
                  <option value="romana">Limba Română</option>
                  <option value="limbi-straine">Limbi Străine</option>
                  <option value="matematica">Matematică</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 mb-1" htmlFor="message">Mesaj</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Scrie mesajul tău aici..."
                  required
                ></textarea>
              </div>

              {/* Hidden fields for additional information */}
              <input type="hidden" name="to_email" value={contactInfo.email} />
              <input type="hidden" name="to_name" value={contactInfo.companyName} />

              {submitStatus.type && (
                <div className={`p-3 rounded-lg ${
                  submitStatus.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {submitStatus.message}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-blue-600 text-white py-2 rounded-lg font-medium transition-colors duration-200 
                  ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-blue-700'}`}
              >
                {isSubmitting ? 'Se trimite...' : 'Trimite mesajul'}
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-6">
            <div className="rounded-[2rem] border border-blue-100 bg-white p-7 shadow-float sm:p-9">
              <h2 className="text-2xl font-semibold mb-4">Informații de contact</h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <MapPin className="h-6 w-6 text-blue-600 mr-4 mt-1" />
                  <div>
                    <p className="font-medium">Adresă</p>
                    <p className="text-gray-600">{contactInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Phone className="h-6 w-6 text-blue-600 mr-4 mt-1" />
                  <div>
                    <p className="font-medium">Telefon</p>
                    {contactInfo.phones.map((phone) => (
                      <a
                        key={phone}
                        href={`tel:${phone.replace(/\s/g, '')}`}
                        className="block text-gray-600 transition-colors hover:text-blue-600"
                      >
                        {phone}
                      </a>
                    ))}
                  </div>
                </div>
                <div className="flex items-center">
                  <Mail className="h-6 w-6 text-blue-600 mr-4" />
                  <div>
                    <p className="font-medium">Email</p>
                    <p className="text-gray-600">{contactInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Clock className="h-6 w-6 text-blue-600 mr-4" />
                  <div>
                    <p className="font-medium">Program</p>
                    <p className="text-gray-600">{contactInfo.program}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-6 mt-4">
                  <a 
                    href={contactInfo.socials.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-gray-600 hover:text-blue-600 transition-colors duration-200"
                  >
                    <Facebook className="h-6 w-6 mr-2" />
                    <span>Facebook</span>
                  </a>
                  <a 
                    href={contactInfo.socials.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-gray-600 hover:text-pink-600 transition-colors duration-200"
                  >
                    <Instagram className="h-6 w-6 mr-2" />
                    <span>Instagram</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Map with reduced height */}
            <div className="rounded-[2rem] border border-blue-100 bg-white p-7 shadow-float">
              <h2 className="text-2xl font-semibold mb-4">Locație</h2>
              <div className="h-48">
                <iframe
                  src={contactInfo.mapUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </div>

        {/* Galerie Foto cu noul Carousel */}
        <div className="mt-8">
          <p className="eyebrow">Din timpul lecțiilor</p>
          <h2 className="mt-4 mb-8 text-4xl font-bold">Centrul Progres, în imagini.</h2>
          <ImageCarousel />
        </div>
      </div>
    </div>
  );
};

export default Contact;
